import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

export type ComplianceTier = 'GOUD' | 'ZILVER' | 'BRONS' | 'ONGEVERIFIEERD' | 'NON_COMPLIANT_HOLD';
type MedailleTier = 'GOUD' | 'ZILVER' | 'BRONS';

/** De vier screeningsindicatoren op het lint; de backend bepaalt ze (tierberekening). */
export interface TierIndicatoren {
  kvk: boolean;
  polis: boolean;
  arbo: boolean;
  register: boolean;
}

export const isMedailleTier = (t?: string | null): t is MedailleTier =>
  t === 'GOUD' || t === 'ZILVER' || t === 'BRONS';

/** Metaalverloop per tier: licht → basis → donker → glans. */
const METAAL: Record<MedailleTier, [string, string, string, string]> = {
  GOUD: ['#f7e7a1', '#c9a227', '#8a6d12', '#e8cf6b'],
  ZILVER: ['#f4f6f8', '#c0c7d1', '#7d8794', '#e3e7ec'],
  BRONS: ['#f0c39b', '#b06a3b', '#6e3b1c', '#d9925e'],
};

const ARC_DEFAULT: Record<MedailleTier, string> = {
  GOUD: 'GOUD · REGISTER-GEVERIFIEERD',
  ZILVER: 'ZILVER · ARBO-SPECIALIST',
  BRONS: 'BRONS · BASIS-GEVERIFIEERD',
};

const NAAM_DEFAULT: Record<MedailleTier, string> = { GOUD: 'Goud', ZILVER: 'Zilver', BRONS: 'Brons' };

const INDICATOR_DEFAULT: Record<keyof TierIndicatoren, { titel: string; ok: string; nok: string }> = {
  kvk: { titel: 'KvK & identiteit', ok: 'Geverifieerd', nok: 'Niet geverifieerd' },
  polis: { titel: 'Beroepsaansprakelijkheid', ok: 'Polis geldig', nok: 'Geen geldige polis' },
  arbo: { titel: 'Arbo-vrijstelling', ok: 'Geschikt (art. 31a Wet LB)', nok: 'Regulier WKR' },
  register: { titel: 'Beroepsregister', ok: 'Geverifieerd', nok: 'Niet aangesloten' },
};

const VOLGORDE: (keyof TierIndicatoren)[] = ['kvk', 'polis', 'arbo', 'register'];

/** Lintvorm met zwaluwstaart-inkeping aan de onderkant. */
const LINT_CLIP = 'polygon(0 0, 100% 0, 100% 100%, 50% calc(100% - 22px), 0 100%)';
const LINT_BREEDTE = 240;
const VIEWPORT_MARGE = 8;

/**
 * Compliance Vetting Tier (Goud/Zilver/Brons) — ronde medaille met uitrollend lint.
 *
 * Ruststand: alleen de medaille. Bij hover, toetsenbordfocus of tap rolt het lint
 * met de vier screeningsindicatoren (KvK, polis, arbo-vrijstelling, register)
 * onder de medaille vandaan naar beneden; absoluut gepositioneerd zodat de kaart
 * eromheen niet verspringt. `uitgerold` toont het lint statisch in de flow (cockpit).
 * Geen medaille bij ONGEVERIFIEERD of NON_COMPLIANT_HOLD.
 */
export const ComplianceTierMedaille: React.FC<{
  tier: ComplianceTier;
  indicatoren: TierIndicatoren;
  size?: number;
  className?: string;
  uitgerold?: boolean;
}> = ({ tier, indicatoren, size = 56, className = '', uitgerold = false }) => {
  const { t } = useTranslation('common');
  const id = React.useId().replace(/:/g, '');
  const [open, setOpen] = React.useState(false);
  const [verschuiving, setVerschuiving] = React.useState(0);
  const lintRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Houd het lint binnen de viewport: meet de medaille, niet het (verschoven) lint.
  const pasPositieAan = React.useCallback(() => {
    if (uitgerold || !wrapperRef.current) return;
    const r = wrapperRef.current.getBoundingClientRect();
    const breedte = Math.min(LINT_BREEDTE, window.innerWidth - 2 * VIEWPORT_MARGE);
    const links = r.left + r.width / 2 - breedte / 2;
    const rechts = links + breedte;
    let dx = 0;
    if (links < VIEWPORT_MARGE) dx = VIEWPORT_MARGE - links;
    else if (rechts > window.innerWidth - VIEWPORT_MARGE) dx = window.innerWidth - VIEWPORT_MARGE - rechts;
    setVerschuiving(dx);
  }, [uitgerold]);

  React.useEffect(() => {
    if (!open) return;
    const sluit = (e: MouseEvent | TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', sluit);
    document.addEventListener('touchstart', sluit);
    return () => {
      document.removeEventListener('mousedown', sluit);
      document.removeEventListener('touchstart', sluit);
    };
  }, [open]);

  if (!isMedailleTier(tier)) return null;

  const [licht, basis, donker, glans] = METAAL[tier];
  const naam = t(`tier.naam.${tier}`, NAAM_DEFAULT[tier]);
  const metaalCss = `linear-gradient(135deg, ${licht} 0%, ${basis} 35%, ${donker} 65%, ${glans} 100%)`;

  const medaille = (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className="block select-none drop-shadow-md"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}-metaal`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={licht} />
          <stop offset="35%" stopColor={basis} />
          <stop offset="65%" stopColor={donker} />
          <stop offset="100%" stopColor={glans} />
        </linearGradient>
        <radialGradient id={`${id}-navy`} cx="50%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#1d3f86" />
          <stop offset="60%" stopColor="#13306e" />
          <stop offset="100%" stopColor="#0b1f4d" />
        </radialGradient>
        <path id={`${id}-arc`} d="M 15 100 A 85 85 0 0 0 185 100" />
      </defs>

      <circle cx="100" cy="100" r="99" fill={`url(#${id}-metaal)`} />
      <circle cx="100" cy="100" r="92" fill={`url(#${id}-navy)`} />
      <circle cx="100" cy="100" r="72" fill="none" stroke={`url(#${id}-metaal)`} strokeWidth="2" />

      {/* Schild met vinkje */}
      <path
        d="M100 30 L121 38 V53 C121 66 112 74 100 79 C88 74 79 66 79 53 V38 Z"
        fill="none"
        stroke={`url(#${id}-metaal)`}
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <path
        d="M90 54 L97.5 61.5 L111 47"
        fill="none"
        stroke={`url(#${id}-metaal)`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <text
        x="100"
        y="110"
        textAnchor="middle"
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
        fontWeight="800"
        fontSize="29"
        fill={`url(#${id}-metaal)`}
      >
        BeLagom
      </text>
      <text
        x="100"
        y="127"
        textAnchor="middle"
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
        fontWeight="600"
        fontSize="10.5"
        fill="#e6ebf5"
      >
        Compliance by Code
      </text>

      <text
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
        fontWeight="800"
        fontSize="10.5"
        letterSpacing="0.6"
        fill="#ffffff"
      >
        <textPath href={`#${id}-arc`} startOffset="50%" textAnchor="middle">
          {t(`tier.arc.${tier}`, ARC_DEFAULT[tier])}
        </textPath>
      </text>
    </svg>
  );

  const lint = (
    <div
      ref={lintRef}
      className="p-[3px] shadow-xl"
      style={{ width: `min(${LINT_BREEDTE}px, calc(100vw - ${2 * VIEWPORT_MARGE}px))`, background: metaalCss, clipPath: LINT_CLIP }}
    >
      <ul
        className="bg-gradient-to-b from-[#13306e] to-[#0b1f4d] px-4 pb-9 text-left"
        style={{ clipPath: LINT_CLIP, paddingTop: uitgerold ? size * 0.3 + 8 : size * 0.32 + 8 }}
      >
        {VOLGORDE.map((sleutel, i) => {
          const ok = indicatoren[sleutel];
          const d = INDICATOR_DEFAULT[sleutel];
          return (
            <li
              key={sleutel}
              className="flex items-center gap-3 py-2"
              style={i > 0 ? { borderTop: `1px solid ${basis}55` } : undefined}
            >
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                  ok ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300' : 'border-red-400 bg-red-500/15 text-red-300'
                }`}
              >
                {ok ? <Check size={15} strokeWidth={3} /> : <X size={15} strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-tight text-white">
                  {t(`tier.indicator.${sleutel}.titel`, d.titel)}
                </span>
                <span className={`block text-xs leading-tight ${ok ? 'text-blue-100/80' : 'text-red-200'}`}>
                  {ok ? t(`tier.indicator.${sleutel}.ok`, d.ok) : t(`tier.indicator.${sleutel}.nok`, d.nok)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  if (uitgerold) {
    return (
      <div className={`inline-flex flex-col items-center ${className}`} aria-label={naam}>
        <div className="relative z-[2]">{medaille}</div>
        <div className="relative z-[1]" style={{ marginTop: -size * 0.3 }}>
          {lint}
        </div>
      </div>
    );
  }

  const lintZichtbaar = open
    ? '[clip-path:inset(0_0_0_0)] translate-y-0 opacity-100'
    : '[clip-path:inset(0_0_100%_0)] -translate-y-2 opacity-0 group-hover:[clip-path:inset(0_0_0_0)] group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:[clip-path:inset(0_0_0_0)] group-focus-within:translate-y-0 group-focus-within:opacity-100';

  return (
    <div
      ref={wrapperRef}
      className={`group relative inline-block flex-shrink-0 ${open ? 'z-50' : 'z-10 hover:z-50 focus-within:z-50'} ${className}`}
      onMouseEnter={pasPositieAan}
      onFocus={pasPositieAan}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${naam} — ${t('tier.openHint', 'Bekijk compliance-status')}`}
        title={t('tier.openHint', 'Bekijk compliance-status')}
        className="relative z-[2] cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#c9a227]"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          pasPositieAan();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pasPositieAan();
            setOpen((v) => !v);
          } else if (e.key === 'Escape') {
            setOpen(false);
            (e.currentTarget as HTMLElement).blur();
          }
        }}
      >
        {medaille}
      </div>
      <div
        className="pointer-events-none absolute left-1/2 z-[1] group-hover:pointer-events-auto group-focus-within:pointer-events-auto"
        style={{ top: size * 0.68, transform: `translateX(calc(-50% + ${verschuiving}px))` }}
      >
        <div
          className={`origin-top transition-[clip-path,transform,opacity] duration-[350ms] ease-out motion-reduce:transition-none ${lintZichtbaar} ${
            open ? 'pointer-events-auto' : ''
          }`}
        >
          {lint}
        </div>
      </div>
    </div>
  );
};

export default ComplianceTierMedaille;
